import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Table, Button, Select, Space, message, theme } from 'antd';
import { formatOperationError } from '@/api/formatError';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import {
  SpaceAssignment,
  SpaceAssignmentDisplay,
  SpaceRelation,
  SpaceType,
  getSpaceLevelDisplay,
  getAccessLevelDisplay,
  GET_USER_SPACE_ASSIGNMENTS,
  GetUserSpaceAssignmentsResponse,
  GetUserSpaceAssignmentsVariables,
  REMOVE_USER_FROM_SPACE,
  RemoveUserFromSpaceVariables,
  RemoveUserFromSpaceResponse,
  ASSIGN_USER_TO_SPACE,
  AssignUserToSpaceVariables,
  AssignUserToSpaceResponse,
  AUTH_API_URL,
} from './SpaceAssignments';
import { getSpaceLevelIcon } from './SpaceIcons';
import { AddSpaceModal } from './AddSpaceModal';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';

const { Option } = Select;

// Matches Ant Design Table row height with size="middle" (default padding + line-height)
const TABLE_ROW_HEIGHT = 47;

interface SpaceAssignmentTableProps {
  userId: string;
  userRole?: string;
  readOnly?: boolean;
  onUpdate?: () => void;
}

// Format the space path with icons
const formatSpacePath = (path: string): React.ReactNode => {
  const parts = path.split(' > ');
  const levels: SpaceType[] = ['site', 'building', 'floor', 'room', 'zone'];

  return (
    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
      {parts.map((part, index) => (
        <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {index > 0 && <span style={{ color: '#9CA3AF' }}>&gt;</span>}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {getSpaceLevelIcon(levels[index] || 'room')}
            <span>{part}</span>
          </span>
        </span>
      ))}
    </span>
  );
};

// Build a lookup map from hierarchy for quick space name/path resolution
interface SpaceLookup {
  name: string;
  path: string;
  type: SpaceType;
}

function buildSpaceLookupMap(sites: SiteOption[]): Map<string, SpaceLookup> {
  const map = new Map<string, SpaceLookup>();

  for (const site of sites) {
    map.set(site.id, {
      name: site.name,
      path: site.name,
      type: 'site',
    });

    for (const building of site.buildings || []) {
      map.set(building.id, {
        name: building.name,
        path: `${site.name} > ${building.name}`,
        type: 'building',
      });

      for (const floor of building.floors || []) {
        map.set(floor.id, {
          name: floor.name,
          path: `${site.name} > ${building.name} > ${floor.name}`,
          type: 'floor',
        });

        for (const room of floor.rooms || []) {
          map.set(room.id, {
            name: room.name,
            path: `${site.name} > ${building.name} > ${floor.name} > ${room.name}`,
            type: 'room',
          });
        }

        for (const zone of floor.zones || []) {
          map.set(zone.id, {
            name: zone.name,
            path: `${site.name} > ${building.name} > ${floor.name} > ${zone.name}`,
            type: 'zone',
          });
        }
      }
    }
  }

  return map;
}

export const SpaceAssignmentTable: React.FC<SpaceAssignmentTableProps> = ({
  userId,
  userRole,
  readOnly = false,
  onUpdate,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [expandColWidth, setExpandColWidth] = useState(48);
  const { token } = theme.useToken();

  const isAdminOrOwner = userRole === 'Admin' || userRole === 'Owner';

  // Fetch space assignments from auth API (skip for admins/owners — they have org-wide access)
  const { data, loading, refetch } = useQuery<
    GetUserSpaceAssignmentsResponse,
    GetUserSpaceAssignmentsVariables
  >(GET_USER_SPACE_ASSIGNMENTS, {
    variables: { user_id: userId },
    context: { uri: AUTH_API_URL },
    skip: isAdminOrOwner,
  });

  // Fetch real site hierarchy for resolving space names (skip for admins/owners)
  const { data: hierarchyData, loading: hierarchyLoading } = useQuery(GET_SITE_HIERARCHY, {
    skip: isAdminOrOwner,
  });

  // Build lookup map for resolving space names from real hierarchy
  const spaceLookup = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return buildSpaceLookupMap(sites);
  }, [hierarchyData]);

  // Mutation to remove a space assignment
  const [removeFromSpace] = useMutation<RemoveUserFromSpaceResponse, RemoveUserFromSpaceVariables>(
    REMOVE_USER_FROM_SPACE,
    { context: { uri: AUTH_API_URL } },
  );

  // Mutation to assign user to space (used for updating relation)
  const [assignToSpace] = useMutation<AssignUserToSpaceResponse, AssignUserToSpaceVariables>(
    ASSIGN_USER_TO_SPACE,
    { context: { uri: AUTH_API_URL } },
  );

  // Convert raw assignments to display format using real hierarchy data
  const toDisplayAssignmentFromHierarchy = useCallback(
    (assignment: SpaceAssignment): SpaceAssignmentDisplay => {
      const lookup = spaceLookup.get(assignment.space_id);
      return {
        ...assignment,
        id: `${assignment.space_type}-${assignment.space_id}-${assignment.relation}`,
        spaceName: lookup?.name || assignment.space_id,
        spacePath: lookup?.path || assignment.space_id,
      };
    },
    [spaceLookup],
  );

  // Convert raw assignments to display format
  const displayAssignments: SpaceAssignmentDisplay[] = useMemo(() => {
    return (data?.userSpaceAssignments || []).map(toDisplayAssignmentFromHierarchy);
  }, [data, toDisplayAssignmentFromHierarchy]);

  const refreshAssignments = () => {
    refetch();
    onUpdate?.();
  };

  const handleUpdateAccess = async (record: SpaceAssignmentDisplay, newRelation: SpaceRelation) => {
    try {
      // Remove old assignment then add new one with updated relation
      await removeFromSpace({
        variables: {
          input: {
            user_id: userId,
            space_type: record.space_type,
            space_id: record.space_id,
            relation: record.relation,
          },
        },
      });
      try {
        await assignToSpace({
          variables: {
            input: {
              user_id: userId,
              space_type: record.space_type,
              space_id: record.space_id,
              relation: newRelation,
            },
          },
        });
      } catch (assignError) {
        // Rollback: re-assign the original relation since removal succeeded
        try {
          await assignToSpace({
            variables: {
              input: {
                user_id: userId,
                space_type: record.space_type,
                space_id: record.space_id,
                relation: record.relation,
              },
            },
          });
          message.error('Failed to update access level. Original access restored.');
        } catch {
          message.error(
            'Failed to update access level and could not restore original access. Please re-add manually.',
          );
        }
        setEditingId(null);
        refreshAssignments();
        return;
      }
      message.success('Access level updated');
      setEditingId(null);
      refreshAssignments();
    } catch (error: unknown) {
      console.error('Error updating access level:', error);
      message.error(formatOperationError('update access level', error));
    }
  };

  const handleDeleteAssignment = async (record: SpaceAssignmentDisplay) => {
    try {
      await removeFromSpace({
        variables: {
          input: {
            user_id: userId,
            space_type: record.space_type,
            space_id: record.space_id,
            relation: record.relation,
          },
        },
      });
      message.success('Space assignment removed');
      refreshAssignments();
    } catch (error: unknown) {
      console.error('Error removing space assignment:', error);
      message.error(formatOperationError('remove space assignment', error));
    }
  };

  const handleAddSuccess = () => {
    refreshAssignments();
  };

  const columns = [
    {
      title: 'Assigned Spaces',
      dataIndex: 'spacePath',
      key: 'spacePath',
      render: (path: string, record: SpaceAssignmentDisplay) => (
        <div>
          <div>{formatSpacePath(path)}</div>
          <div className="text-gray-400 text-xs">ID: {record.space_id}</div>
        </div>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'space_type',
      key: 'space_type',
      render: (type: SpaceType) => getSpaceLevelDisplay(type),
    },
    {
      title: 'Access',
      dataIndex: 'relation',
      key: 'relation',
      render: (relation: SpaceRelation, record: SpaceAssignmentDisplay) => {
        if (readOnly || editingId !== record.id) {
          return getAccessLevelDisplay(relation);
        }
        return (
          <Select
            value={relation}
            onChange={(value) => handleUpdateAccess(record, value)}
            style={{ width: 120 }}
            size="small"
            open
            onBlur={() => setEditingId(null)}
          >
            <Option value="can_edit">Can Edit</Option>
            <Option value="can_view">Can View</Option>
          </Select>
        );
      },
    },
    ...(readOnly
      ? []
      : [
          {
            title: 'Action',
            key: 'action',
            render: (_: unknown, record: SpaceAssignmentDisplay) => (
              <Space>
                <Button type="link" onClick={() => setEditingId(record.id)}>
                  Edit
                </Button>
                <Button type="link" onClick={() => handleDeleteAssignment(record)}>
                  Delete
                </Button>
              </Space>
            ),
          },
        ]),
  ];

  // Sync nested table left padding with the parent expand-icon column width.
  // Relies on Ant Design's internal DOM structure (.ant-table-expanded-row, .ant-table-row-expand-icon-cell).
  // Falls back to 48px if the DOM structure changes (e.g., after an Ant Design upgrade).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const findAndObserve = () => {
      const expandedRow = el.closest('.ant-table-expanded-row');
      const parentTable = expandedRow?.closest('.ant-table-wrapper');
      const expandCell = parentTable?.querySelector<HTMLElement>('.ant-table-row-expand-icon-cell');
      if (expandCell) {
        const updateWidth = () => setExpandColWidth(expandCell.offsetWidth);
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(expandCell);
        return () => observer.disconnect();
      }
      return undefined;
    };

    // Try immediately, then retry on next frame if the parent DOM isn't ready yet
    let cleanup = findAndObserve();
    if (!cleanup) {
      const rafId = requestAnimationFrame(() => {
        cleanup = findAndObserve();
      });
      return () => {
        cancelAnimationFrame(rafId);
        cleanup?.();
      };
    }
    return cleanup;
  }, []);

  if (userRole === 'Admin' || userRole === 'Owner') {
    return null;
  }

  return (
    <div ref={wrapperRef} className="-mx-4 -my-4" style={{ paddingLeft: expandColWidth }}>
      <Table
        columns={columns}
        dataSource={displayAssignments}
        rowKey="id"
        pagination={false}
        size="middle"
        loading={loading || hierarchyLoading}
        locale={{ emptyText: 'No spaces assigned' }}
        showSorterTooltip={false}
      />

      {!readOnly && (
        <div
          className="flex items-center px-4"
          style={{ height: TABLE_ROW_HEIGHT, backgroundColor: token.colorBgLayout }}
        >
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalOpen(true)}
            className="border-gray-300"
          >
            Assign Space
          </Button>
        </div>
      )}

      {/* Add Space Modal */}
      <AddSpaceModal
        open={isAddModalOpen}
        userId={userId}
        onCancel={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};

export default SpaceAssignmentTable;
